import { useState } from "react";
import { Card, CardContent, CardHeader } from "../../../../ui/card";
import { Button } from "../../../../ui/button";
import { Badge } from "../../../../ui/badge";
import { Progress } from "../../../../ui/progress";
import { Edit, Trash2, RefreshCw, Copy } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "../../../../../hooks/use-toast";
import { useOutletRefresh } from "../../../../../hooks/useOutletRefresh";
import AutoSyncSettings from "./AutoSyncSettings";

interface Outlet {
  id: string;
  outlet_name: string;
  location?: string;
  google_maps_url?: string;
  instagram_username?: string;
  tiktok_username?: string;
  facebook_url?: string;
  tripadvisor_url?: string;
  scrape_enabled?: boolean;
  fetch_limit?: number;
  created_at: string;
  data_source?: string;
  verified_status?: boolean;
  data_fetch_mode?: string;
  place_id?: string;
  auto_sync_enabled?: boolean;
  auto_sync_frequency?: string;
  auto_sync_platforms?: any;
  last_auto_sync?: string | null;
}

interface OutletCardProps {
  outlet: Outlet;
  onEdit: (outlet: Outlet) => void;
  onDelete: (id: string) => void;
  onRefresh?: () => void;
}

const OutletCard = ({ outlet, onEdit, onDelete, onRefresh }: OutletCardProps) => {
  const { toast } = useToast();
  const { refreshOutlet, isRefreshing } = useOutletRefresh();
  const [progress, setProgress] = useState(0);
  
  const isDataForSEO = outlet.data_source === 'dataforseo';
  
  const connectedPlatforms = [
    { name: "Google Maps", icon: "🗺️", connected: !!(outlet.google_maps_url || outlet.place_id), color: "bg-blue-500" },
    { name: "Instagram", icon: "📷", connected: !!outlet.instagram_username, color: "bg-purple-500" },
    { name: "TikTok", icon: "🎵", connected: !!outlet.tiktok_username, color: "bg-gray-800" },
    { name: "Facebook", icon: "👍", connected: !!outlet.facebook_url, color: "bg-blue-600" },
    { name: "Tripadvisor", icon: "✈️", connected: !!outlet.tripadvisor_url, color: "bg-green-600" },
  ].filter(p => p.connected);
  
  const platformCount = connectedPlatforms.length || 1;

  const handleRefreshReviews = async () => {
    if (!outlet.scrape_enabled) {
      toast({
        title: "Scraping Disabled",
        description: "Enable scraping for this outlet first",
        variant: "destructive",
      });
      return;
    }

    if (!isDataForSEO && connectedPlatforms.length === 0) {
      toast({
        title: "No Platforms",
        description: "Connect at least one platform to scrape reviews",
        variant: "destructive",
      });
      return;
    }

    setProgress(0);

    await refreshOutlet({
      outletId: outlet.id,
      outletName: outlet.outlet_name,
      isDataForSEO,
      onProgress: setProgress,
      onSuccess: () => {
        onRefresh?.();
        setTimeout(() => setProgress(0), 1000);
      },
    });
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">{outlet.outlet_name}</h3>
            {outlet.location && (
              <p className="text-sm text-muted-foreground mt-1">{outlet.location}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-muted-foreground font-mono">
                ID: {outlet.id.slice(0, 8)}...
              </p>
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5"
                onClick={() => {
                  navigator.clipboard.writeText(outlet.id);
                  toast({
                    title: "Copied!",
                    description: "Outlet ID copied to clipboard",
                  });
                }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Added {format(new Date(outlet.created_at), "dd/MM/yyyy")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="icon"
              variant="outline"
              onClick={() => onEdit(outlet)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="outline"
              onClick={() => onDelete(outlet.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Scraping Status */}
          <div className="flex items-center justify-between p-2 bg-muted rounded-md">
            <span className="text-sm font-medium">Scraping Status:</span>
            <Badge variant={outlet.scrape_enabled !== false ? "default" : "secondary"}>
              {outlet.scrape_enabled !== false ? "✅ Enabled" : "⏸️ Disabled"}
            </Badge>
          </div>

          {/* Fetch Limit Override */}
          {outlet.fetch_limit && (
            <div className="flex items-center justify-between p-2 bg-muted rounded-md">
              <span className="text-sm font-medium">Custom Fetch Limit:</span>
              <Badge variant="outline">{outlet.fetch_limit} reviews</Badge>
            </div>
          )}

          {/* Connected Platforms */}
          <div>
            <p className="text-sm font-medium mb-2">Connected Platforms:</p>
            <div className="flex flex-wrap gap-2">
              {connectedPlatforms.length > 0 ? (
                connectedPlatforms.map((platform) => (
                  <Badge
                    key={platform.name}
                    className={`${platform.color} text-white`}
                  >
                    {platform.icon} {platform.name}
                  </Badge>
                ))
              ) : isDataForSEO ? (
                <div className="flex flex-col gap-1">
                  <Badge className="bg-green-600 text-white">
                    ✅ Connected via DataForSEO {outlet.data_fetch_mode === 'place_id' ? '(Place ID)' : '(Keyword)'}
                  </Badge>
                  {outlet.place_id && (
                    <span className="text-xs text-muted-foreground">
                      Place ID: {outlet.place_id}
                    </span>
                  )}
                </div>
              ) : (
                <Badge variant="outline">No Platforms Connected</Badge>
              )}
            </div>
            {isDataForSEO && outlet.place_id && connectedPlatforms.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Place ID: {outlet.place_id}
              </p>
            )}
          </div>

          {/* Refresh Reviews Button */}
          {outlet.scrape_enabled !== false && (isDataForSEO || connectedPlatforms.length > 0) && (
            <div className="space-y-2">
              <Button
                onClick={handleRefreshReviews}
                disabled={isRefreshing}
                className="w-full"
                variant="outline"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? `Refreshing ${platformCount} platform${platformCount !== 1 ? 's' : ''}...` : "Refresh Reviews"}
              </Button>
              {isRefreshing && (
                <div className="space-y-1">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-center text-muted-foreground">
                    {Math.round(progress)}% complete
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Auto Sync Settings */}
          <AutoSyncSettings
            outletId={outlet.id}
            autoSyncEnabled={outlet.auto_sync_enabled ?? false}
            autoSyncFrequency={outlet.auto_sync_frequency ?? "daily"}
            autoSyncPlatforms={
              Array.isArray(outlet.auto_sync_platforms)
                ? outlet.auto_sync_platforms
                : ["google"]
            }
            lastAutoSync={outlet.last_auto_sync}
            onUpdated={onRefresh}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default OutletCard;
